<template>
  <VCol class="d-flex pa-0">
    <template v-if="!mobileMenu">
      <VMenu
         v-if="showAccountLink || isAgent"
         v-model="showUserMenu"
         transition="none"
         :scrim="false"
         location-strategy="connected"
         :width="userMenuMinWidth"
         z-index="2147483642"
        >
        <template #activator="{ props: menuProps }">
          <button
            v-bind="menuProps"
            type="button"
            class="icon-btn"
            :aria-label="$t('page.account.title')"
            data-testid="user-drop-down"
          >
            <SIcon size="24" :icon="(showAccountLink || isAgent) ? icons.mdiAccountCircle : icons.mdiAccountCircleOutline" decorative />
            <SIcon v-if="showAccountLink || isAgent" :icon="icons.mdiMenuDown" decorative />
          </button>
        </template>
        <template #default>
          <VList density="compact" class="user-menu-list pt-0">
            <VListItem class="text-secondary background-grey font-weight-medium"><span class=" text-capitalize" >{{
                $t('user.label.greeting')
              }}</span>, <span class="text-secondary font-weight-medium active-bolder text-capitalize" data-testid="menu-user-firstname">{{ userFirstName || 'Agent' }}</span>
            </VListItem>
            <VDivider />
            <UserMenuContent />
          </VList>
          </template>
     </VMenu>
    </template>
    <VList
      v-else-if="showAccountLink || isAgent"
      class="user-menu-list mobile-user-menu pt-0"
      bg-color="backgroundDiv"
      width="100%"
    >
      <button
        type="button"
        class="v-list-item v-list-item--link font-weight-medium d-flex align-center w-100 bg-transparent"
        :aria-expanded="expandMobileUserView"
        @click="expandMobileUserView = !expandMobileUserView"
      >
        <span class="text-capitalize text-secondary">{{ $t('user.label.greeting') }}</span><span class="text-secondary">,</span>&nbsp;<span class="text-secondary font-weight-medium active-bolder text-capitalize" data-testid="menu-user-firstname">{{ userFirstName || 'Agent' }}</span>
        <VIcon color="black" size="24" class="ml-auto">
          {{ expandMobileUserView ? icons.mdiChevronUp : icons.mdiChevronDown }}
        </VIcon>
      </button>
      <UserMenuContent v-if="expandMobileUserView" class="mobile-account-content" mobile-menu />
    </VList>
    <template v-if="!isAgent">
      <SBtn
          v-if="!showAccountLink"
          :text="!mobileMenu"
          :block="mobileMenu"
          rounded
          :variant="mobileMenu ? 'outlined' : undefined"
          :class="mobileMenu ? 'text-none' : 'pr-1 text-capitalize'"
          :color="mobileMenu ? 'secondary' : undefined"
          data-testid="menu-user-btn-signin"
          @click="showSignIn"
      >
        <span v-if="mobileMenu" class="font-weight-bold">{{ $t('btn.signInCreateAccount') }}</span>
        <span v-else class="link-hover-underline signin-text"><VIcon size="28" color="primary" class="pr-1">{{ icons.mdiAccountCircleOutline }}</VIcon>{{ $t('account.signin') }}</span>
      </SBtn>
    </template>
  </VCol>
  <!-- Need v-if to destroy dom element when closed.  Otherwise, dialog can be hidden behind other dialogs (e.g. wishlist for unauthenticated user) -->
  <!-- Only the non-mobileMenu instance owns these dialogs so they aren't duplicated when UserMenu also renders inside the mobile drawer -->
  <AppAuthDialog
      v-if="authMenu && !mobileMenu"
      v-model="authMenu"
      data-testid="auth-dialog"
      :demo="demoSpecificLogin"
      :register="false"
      color="black"
      register-btn
      @authenticated="onAuthenticated"
  />
  <TimeoutDialog v-if="!mobileMenu"/>
</template>

<script lang="ts" setup>
import { mdiAccountCircleOutline, mdiAccountCircle, mdiMenuDown, mdiChevronDown, mdiChevronUp } from '@mdi/js'
import SIcon from '~/components/SIcon.vue'
import AppAuthDialog from '~/components/auth/AuthDialog.vue'
import TimeoutDialog from '~/components/auth/TimeoutDialog.vue'
import UserMenuContent from '~/components/account/user-menu/UserMenuContent.vue'
import {UserRole} from '~/types/account'
import {useAuthStore} from '~/stores/auth'
import {useSAuth} from "~/composables/auth";
import {useHeaderLayers} from '~/composables/useHeaderLayers'

interface Props {
  mobileMenu?: boolean
}

withDefaults(defineProps<Props>(), {
  mobileMenu: false
})

const authStore = useAuthStore()
const route = useRoute()
const layers = useHeaderLayers()

const authMenu = computed({
  get() {
    return authStore.authMenu
  },
  set(val) {
    authStore.updateAuthMenu(val)
  }
})

// True when arriving from the demo portal (?from=demoportal). Passed to the
// auth dialog so sign-in shows the demo heading/subheading and hides the
// welcome-back line and registration.
const demoSpecificLogin = computed(() => route.query?.from === 'demoportal')

const isAgent = computed(() => authStore.isAgent)

const icons = { mdiAccountCircleOutline, mdiAccountCircle, mdiMenuDown, mdiChevronDown, mdiChevronUp }
const userMenuMinWidth = ref(200)
const showUserMenu = ref(false)
const expandMobileUserView = ref(false)

const auth = useSAuth()

const showSignIn = () => {
  authStore.updateAuthMenu(!authMenu.value)
}

const isAuthenticated = computed(() => auth.isAuthenticated.value)

const showAccountLink = computed(() => isAuthenticated.value && (auth.userRole.value === UserRole.Customer || auth.userRole.value === UserRole.Demonstrator))

const userFirstName = computed(() => auth.user.value?.firstName)

const onAuthenticated = () => {
  authStore.updateAuthMenu(false)
}

watch(showUserMenu, (open) => {
  if (open) {
    layers.open('accountMenu')
  } else {
    layers.close('accountMenu')
  }
})

watch(authMenu, (open) => {
  if (open) {
    layers.open('authDialog')
  } else {
    layers.close('authDialog')
  }
})

watch(layers.activeLayer, (layer) => {
  if (layer !== 'accountMenu' && showUserMenu.value) {
    showUserMenu.value = false
  }
})

// Open the CUSTOMER sign-in dialog when arriving with ?signIn=customer. Used by the
// agent sign-in screen's "shopping for yourself?" link: an employee who wants to shop
// with their staff discount lands on the market homepage and the dialog opens here.
// immediate handles a direct load / SSR hydration on ?signIn=customer; the watch handles
// SPA navigation onto it.
watch(() => route.query.signIn, (value) => {
  if (value === 'customer') {
    authStore.updateAuthMenu(true)
  }
}, { immediate: true })

// Open the DEMO sign-in dialog when arriving with ?from=demoportal. immediate
// handles a direct load; the watch handles SPA navigation onto it. Closing the
// dialog does not clear the query, but the watcher only fires on query change,
// so it does not reopen -- matching nuxt2's one-shot init flag.
watch(() => route.query.from, (value) => {
  if (value === 'demoportal') {
    authStore.updateAuthMenu(true)
  }
}, { immediate: true })
</script>

<style lang='scss' scoped>
.link-hover-underline:hover {
  text-decoration: underline;
}
.signin-text {
  font-weight: 500;
  font-size: 1.143rem;
}
.user-menu-list {
  border-top-left-radius: 0px;
  border-top-right-radius: 0px;

  :deep(.v-list-item) {
    font-size: 1rem;
  }
}
// Drops the desktop dropdown clear of the header. Not the drawer: there the list
// is in flow, so the offset left a gap above it and overlapped the row below.
.user-menu-list:not(.mobile-user-menu) {
  margin-top: 2px;
  top: 16px;
}
.user-menu-list.mobile-user-menu {
  > :deep(.v-list-item) {
    min-height: 48px;
    font-size: 1.143rem;
    font-weight: 600;
    padding-left: 24px;
    padding-right: 24px;
  }
}
.mobile-user-menu :deep(.v-list-item) {
  min-height: 48px;
}
// Indent the drawer's account links under the greeting toggle (nuxt2 parity).
.mobile-account-content :deep(.v-list-item) {
  padding-left: 32px;
}
.background-grey {
  background-color: rgb(var(--v-theme-backgroundDiv));
}
</style>
